import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, PointerEvent as ReactPointerEvent } from "react";
import { Modal } from "./Modal";
import { IconCrop, IconDroplet, IconSliders, IconRotateCcw, IconRotateCw } from "./icons";
import { useImageEdit } from "../hooks/useImageEdit";
import type { EditedImage } from "../hooks/useImageEdit";
import type { AspectRatio, ImageCrop, ImageEdit } from "../utils/imageEdit";
import {
  ADJUSTMENTS,
  ASPECT_RATIOS,
  FILTER_PRESETS,
  FULL_CROP,
  MIN_CROP_SIZE,
  NEUTRAL_EDIT,
  applyPreset,
  cssFilter,
  cssTransform,
  hasAnyChange,
  isQuarterTurned,
} from "../utils/imageEdit";

interface ImageEditorModalProps {
  open: boolean;
  onClose: () => void;
  /** S3 key of the image to edit — what the server renders from. */
  imageKey: string | null;
  /**
   * A URL the browser can display it from. Either the presigned url a stored
   * asset already carries, or an object URL for a file that was just picked —
   * the editor only ever *shows* this, never reads its pixels, so a
   * cross-origin presigned URL is fine.
   */
  url: string | null;
  fileName: string;
  /** Handed the newly rendered image. The caller decides what to point at it. */
  onApplied: (result: EditedImage) => void;
  title?: string;
}

type Tab = "crop" | "filters" | "adjust";

const TABS: { id: Tab; label: string; Icon: React.FC<{ size?: number }> }[] = [
  { id: "crop", label: "Crop", Icon: IconCrop },
  { id: "filters", label: "Filters", Icon: IconDroplet },
  { id: "adjust", label: "Adjust", Icon: IconSliders },
];

/** Which edge(s) a drag on the crop box moves. "move" slides the whole box. */
type CropHandle = "move" | "n" | "s" | "e" | "w" | "ne" | "nw" | "se" | "sw";

const HANDLES: CropHandle[] = ["nw", "n", "ne", "e", "se", "s", "sw", "w"];

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

// Smallest the crop box may get on screen, in px. MIN_CROP_SIZE alone is 2% of
// the image, which on a normal stage is a handful of pixels — small enough that
// the box reads as gone and leaves no edge big enough to grab and drag back.
const MIN_CROP_PX = 24;

/** The floor above as a fraction of the image, never below the server's own. */
const minCropFraction = (extentPx: number) =>
  extentPx > 0 ? Math.max(MIN_CROP_SIZE, Math.min(0.9, MIN_CROP_PX / extentPx)) : MIN_CROP_SIZE;

/**
 * Reshapes `crop` to the given display aspect ratio, keeping it centred on
 * where it already is. The ratio is in displayed pixels while the crop is
 * normalized, so the image's own proportions have to come into it — hence
 * `frameAspect` (displayed width / displayed height of the whole image).
 */
const fitCropToAspect = (crop: ImageCrop, aspect: number, frameAspect: number): ImageCrop => {
  // A normalized box (w, h) shows at an aspect of (w * frameAspect) / h, so
  // the height this width implies is:
  let width = crop.width;
  let height = (width * frameAspect) / aspect;
  if (height > 1) {
    height = 1;
    width = (height * aspect) / frameAspect;
  }
  if (width > 1) {
    width = 1;
    height = (width * frameAspect) / aspect;
  }
  const centreX = crop.x + crop.width / 2;
  const centreY = crop.y + crop.height / 2;
  return {
    width,
    height,
    x: clamp(centreX - width / 2, 0, 1 - width),
    y: clamp(centreY - height / 2, 0, 1 - height),
  };
};

/**
 * Full image editor: crop, rotate, flip, filter presets and per-channel
 * adjustments, previewed live and rendered server-side on Apply.
 *
 * Nothing here touches pixels. The preview is the browser's own `filter` and
 * `transform` applied to an <img>, and Apply sends the same numbers to
 * `editImage`, which reproduces them with the equivalent ffmpeg filters. That
 * is what lets this edit any stored image without downloading it, and why the
 * result matches what was on screen (see utils/imageEdit.ts).
 */
export const ImageEditorModal = ({
  open,
  onClose,
  imageKey,
  url,
  fileName,
  onApplied,
  title,
}: ImageEditorModalProps) => {
  const { applyEdit, editing, error } = useImageEdit();

  const [edit, setEdit] = useState<ImageEdit>(NEUTRAL_EDIT);
  const [tab, setTab] = useState<Tab>("crop");
  const [aspect, setAspect] = useState<AspectRatio>(ASPECT_RATIOS[0]);
  const [presetId, setPresetId] = useState("original");
  /** Natural pixel size of the source, needed to lay the stage out. */
  const [natural, setNatural] = useState<{ width: number; height: number } | null>(null);
  const [stage, setStage] = useState<{ width: number; height: number }>({ width: 0, height: 0 });

  const stageRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);

  // Start clean every time the editor opens on something — it stays mounted
  // between opens, so a previous session's crop would otherwise carry over.
  useEffect(() => {
    if (!open) return;
    setEdit(NEUTRAL_EDIT);
    setTab("crop");
    setAspect(ASPECT_RATIOS[0]);
    setPresetId("original");
    setNatural(null);
  }, [open, imageKey]);

  // The stage is flexible, so its size has to be measured rather than assumed —
  // every crop coordinate below is derived from it.
  useLayoutEffect(() => {
    if (!open) return;
    const element = stageRef.current;
    if (!element) return;
    const measure = () =>
      setStage({ width: element.clientWidth, height: element.clientHeight });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [open]);

  const quarterTurned = isQuarterTurned(edit);

  // Size of the image *after* rotation, in source pixels.
  const rotatedWidth = natural ? (quarterTurned ? natural.height : natural.width) : 0;
  const rotatedHeight = natural ? (quarterTurned ? natural.width : natural.height) : 0;

  // Fit the rotated image inside the stage. `scale` doubles as the factor that
  // converts a source-pixel blur radius into displayed pixels.
  const scale =
    rotatedWidth && rotatedHeight && stage.width && stage.height
      ? Math.min(stage.width / rotatedWidth, stage.height / rotatedHeight)
      : 0;
  const displayWidth = rotatedWidth * scale;
  const displayHeight = rotatedHeight * scale;
  const frameAspect = displayHeight > 0 ? displayWidth / displayHeight : 1;

  const filter = cssFilter(edit, scale || 1);
  const transform = cssTransform(edit);

  // --- Crop dragging -------------------------------------------------------

  const dragRef = useRef<{ handle: CropHandle; startX: number; startY: number; crop: ImageCrop } | null>(
    null,
  );

  const beginDrag = (handle: CropHandle) => (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!displayWidth || !displayHeight) return;
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = {
      handle,
      startX: event.clientX,
      startY: event.clientY,
      crop: edit.crop,
    };
  };

  const onDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || !displayWidth || !displayHeight) return;

    // Pointer travel, expressed in the same normalized units as the crop.
    const dx = (event.clientX - drag.startX) / displayWidth;
    const dy = (event.clientY - drag.startY) / displayHeight;
    const start = drag.crop;

    if (drag.handle === "move") {
      setEdit((prev) => ({
        ...prev,
        crop: {
          ...start,
          x: clamp(start.x + dx, 0, 1 - start.width),
          y: clamp(start.y + dy, 0, 1 - start.height),
        },
      }));
      return;
    }

    // Resize: move only the edges this handle owns, keeping the opposite ones
    // pinned, then re-impose the aspect ratio if one is locked.
    const minWidth = minCropFraction(displayWidth);
    const minHeight = minCropFraction(displayHeight);

    let left = start.x;
    let top = start.y;
    let right = start.x + start.width;
    let bottom = start.y + start.height;

    if (drag.handle.includes("w")) left = clamp(start.x + dx, 0, right - minWidth);
    if (drag.handle.includes("e")) right = clamp(right + dx, left + minWidth, 1);
    if (drag.handle.includes("n")) top = clamp(start.y + dy, 0, bottom - minHeight);
    if (drag.handle.includes("s")) bottom = clamp(bottom + dy, top + minHeight, 1);

    let next: ImageCrop = { x: left, y: top, width: right - left, height: bottom - top };

    if (aspect.value) {
      // Drive the ratio off whichever dimension this handle actually changed,
      // so a side handle doesn't fight the pointer.
      const drivenByWidth = drag.handle === "e" || drag.handle === "w" || drag.handle.length === 2;
      if (drivenByWidth) {
        const height = clamp((next.width * frameAspect) / aspect.value, minHeight, 1);
        const anchorTop = drag.handle.includes("n") ? bottom - height : top;
        next = { ...next, height, y: clamp(anchorTop, 0, 1 - height) };
      } else {
        const width = clamp((next.height * aspect.value) / frameAspect, minWidth, 1);
        const anchorLeft = drag.handle.includes("w") ? right - width : left;
        next = { ...next, width, x: clamp(anchorLeft, 0, 1 - width) };
      }
      // The ratio correction can push the box past an edge; pull it back in.
      next.x = clamp(next.x, 0, 1 - next.width);
      next.y = clamp(next.y, 0, 1 - next.height);
    }

    setEdit((prev) => ({ ...prev, crop: next }));
  };

  const endDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    dragRef.current = null;
  };

  // --- Controls ------------------------------------------------------------

  const chooseAspect = useCallback(
    (ratio: AspectRatio) => {
      setAspect(ratio);
      // Fitting needs the image's real proportions; before it has loaded,
      // frameAspect is a placeholder 1 and would shape the box wrongly. The
      // ratio is still recorded, so the first drag applies it correctly.
      if (!ratio.value || !displayWidth) return;
      setEdit((prev) => ({ ...prev, crop: fitCropToAspect(prev.crop, ratio.value!, frameAspect) }));
    },
    [frameAspect, displayWidth],
  );

  // Rotating swaps the frame's proportions, so a locked ratio has to be
  // re-imposed against the new frame — and a free crop is simply reset,
  // because its old normalized box means something different now.
  const rotateBy = (degrees: number) =>
    setEdit((prev) => ({
      ...prev,
      rotate: (((prev.rotate + degrees) % 360) + 360) % 360,
      crop: FULL_CROP,
    }));

  const setAdjustment = (key: keyof ImageEdit, value: number) => {
    setEdit((prev) => ({ ...prev, [key]: value }));
    // The sliders and the presets edit the same numbers, so once a slider
    // moves, no preset accurately describes the image any more.
    setPresetId("custom");
  };

  const choosePreset = (id: string) => {
    const preset = FILTER_PRESETS.find((p) => p.id === id);
    if (!preset) return;
    setPresetId(id);
    setEdit((prev) => applyPreset(prev, preset));
  };

  const resetAll = () => {
    setEdit(NEUTRAL_EDIT);
    setAspect(ASPECT_RATIOS[0]);
    setPresetId("original");
  };

  const dirty = hasAnyChange(edit);

  const handleApply = async () => {
    if (!imageKey) return;
    const result = await applyEdit(imageKey, fileName, edit);
    if (result) {
      onApplied(result);
      onClose();
    }
  };

  const previewStyle: CSSProperties = useMemo(
    () => ({
      // A quarter turn rotates the element about its centre without changing
      // its layout box, so the box is laid out pre-rotation (dimensions
      // swapped) and centred inside the post-rotation canvas.
      width: quarterTurned ? displayHeight : displayWidth,
      height: quarterTurned ? displayWidth : displayHeight,
      transform: `translate(-50%, -50%) ${transform === "none" ? "" : transform}`.trim(),
      filter,
    }),
    [quarterTurned, displayWidth, displayHeight, transform, filter],
  );

  const cropStyle: CSSProperties = {
    left: `${edit.crop.x * 100}%`,
    top: `${edit.crop.y * 100}%`,
    width: `${edit.crop.width * 100}%`,
    height: `${edit.crop.height * 100}%`,
  };

  return (
    <Modal
      open={open}
      title={title ?? `Edit ${fileName}`}
      onClose={onClose}
      xwide
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={resetAll} disabled={editing || !dirty}>
            Reset
          </button>
          <button type="button" className="btn" onClick={onClose} disabled={editing}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={handleApply}
            disabled={editing || !dirty || !imageKey}
          >
            {editing ? "Applying…" : "Apply"}
          </button>
        </>
      }
    >
      {error ? <div className="form-error">{error}</div> : null}

      <div className="imgedit">
        <div className="imgedit__stage" ref={stageRef}>
          {url ? (
            <div
              className="imgedit__canvas"
              ref={canvasRef}
              style={{ width: displayWidth || undefined, height: displayHeight || undefined }}
            >
              <img
                className="imgedit__image"
                src={url}
                alt={fileName}
                style={previewStyle}
                draggable={false}
                onLoad={(e) =>
                  setNatural({
                    width: e.currentTarget.naturalWidth,
                    height: e.currentTarget.naturalHeight,
                  })
                }
              />

              {/* Shown on every tab, not just Crop, so the framing stays
                  visible while filters and sliders are being tuned. */}
              {displayWidth > 0 ? (
                <div
                  className="imgedit__crop"
                  style={cropStyle}
                  onPointerDown={beginDrag("move")}
                  onPointerMove={onDrag}
                  onPointerUp={endDrag}
                  onPointerCancel={endDrag}
                >
                  <span className="imgedit__grid" aria-hidden="true" />
                  {HANDLES.map((handle) => (
                    <span
                      key={handle}
                      className={`imgedit__handle imgedit__handle--${handle}`}
                      onPointerDown={beginDrag(handle)}
                      onPointerMove={onDrag}
                      onPointerUp={endDrag}
                      onPointerCancel={endDrag}
                    />
                  ))}
                </div>
              ) : null}
            </div>
          ) : (
            <p className="panel__sub">No image to edit.</p>
          )}
        </div>

        <div className="imgedit__panel">
          <div className="imgedit__tabs" role="tablist">
            {TABS.map(({ id, label, Icon }) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={tab === id}
                className={`imgedit__tab${tab === id ? " imgedit__tab--active" : ""}`}
                onClick={() => setTab(id)}
              >
                <Icon size={14} />
                {label}
              </button>
            ))}
          </div>

          <div className="imgedit__panel-body">
            {tab === "crop" ? (
              <>
                <p className="imgedit__label">Aspect ratio</p>
                <div className="imgedit__chips">
                  {ASPECT_RATIOS.map((ratio) => (
                    <button
                      key={ratio.id}
                      type="button"
                      className={`imgedit__chip${aspect.id === ratio.id ? " imgedit__chip--active" : ""}`}
                      onClick={() => chooseAspect(ratio)}
                    >
                      {ratio.label}
                    </button>
                  ))}
                </div>

                <p className="imgedit__label">Rotate &amp; flip</p>
                <div className="imgedit__chips">
                  <button type="button" className="imgedit__chip" onClick={() => rotateBy(-90)}>
                    <IconRotateCcw size={14} /> Left
                  </button>
                  <button type="button" className="imgedit__chip" onClick={() => rotateBy(90)}>
                    <IconRotateCw size={14} /> Right
                  </button>
                  <button
                    type="button"
                    className={`imgedit__chip${edit.flipHorizontal ? " imgedit__chip--active" : ""}`}
                    onClick={() => setEdit((prev) => ({ ...prev, flipHorizontal: !prev.flipHorizontal }))}
                  >
                    Flip H
                  </button>
                  <button
                    type="button"
                    className={`imgedit__chip${edit.flipVertical ? " imgedit__chip--active" : ""}`}
                    onClick={() => setEdit((prev) => ({ ...prev, flipVertical: !prev.flipVertical }))}
                  >
                    Flip V
                  </button>
                </div>

                <button
                  type="button"
                  className="btn btn--ghost imgedit__wide-btn"
                  onClick={() => setEdit((prev) => ({ ...prev, crop: FULL_CROP }))}
                >
                  Reset crop
                </button>

                <p className="imgedit__hint">
                  Drag inside the box to move it, or its corners and edges to resize.
                  {natural ? ` Source is ${natural.width}×${natural.height}px.` : ""}
                </p>
              </>
            ) : null}

            {tab === "filters" ? (
              <div className="imgedit__presets">
                {FILTER_PRESETS.map((preset) => (
                  <button
                    key={preset.id}
                    type="button"
                    className={`imgedit__preset${presetId === preset.id ? " imgedit__preset--active" : ""}`}
                    onClick={() => choosePreset(preset.id)}
                  >
                    <span className="imgedit__preset-thumb">
                      {url ? (
                        <img
                          src={url}
                          alt=""
                          draggable={false}
                          // Colour only — leaving the crop and rotation out
                          // keeps the strip stable while they are being
                          // changed, and the preset never touches them anyway.
                          style={{ filter: cssFilter({ ...NEUTRAL_EDIT, ...preset.values }) }}
                        />
                      ) : null}
                    </span>
                    <span className="imgedit__preset-label">{preset.label}</span>
                  </button>
                ))}
              </div>
            ) : null}

            {tab === "adjust" ? (
              <>
                {ADJUSTMENTS.map((adjustment) => {
                  const value = edit[adjustment.key];
                  return (
                    <div key={adjustment.key} className="imgedit__slider">
                      <div className="imgedit__slider-head">
                        <span>{adjustment.label}</span>
                        <button
                          type="button"
                          className="imgedit__slider-value"
                          title="Reset to default"
                          onClick={() => setAdjustment(adjustment.key, adjustment.neutral)}
                        >
                          {adjustment.format(value)}
                        </button>
                      </div>
                      <input
                        type="range"
                        min={adjustment.min}
                        max={adjustment.max}
                        step={adjustment.step}
                        value={value}
                        onChange={(e) => setAdjustment(adjustment.key, Number(e.target.value))}
                      />
                    </div>
                  );
                })}
                <p className="imgedit__hint">Click a value to reset that slider.</p>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </Modal>
  );
};
